import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Car, Image, KeyRound, Inbox, BellRing, Globe, CalendarCheck, Clock, Newspaper, BadgePercent, Info } from "lucide-react";
import AdminVehicles from "@/components/admin/AdminVehicles";
import AdminBlog from "@/components/admin/AdminBlog";
import AdminBenefits from "@/components/admin/AdminBenefits";
import AdminAbout from "@/components/admin/AdminAbout";
import AdminCategories from "@/components/admin/AdminCategories";
import AdminApplications from "@/components/admin/AdminApplications";
import AdminNotifications from "@/components/admin/AdminNotifications";
import AdminSiteSettings from "@/components/admin/AdminSiteSettings";
import AdminRequests from "@/components/admin/AdminRequests";
import { API } from "@/lib/api";

const TOKEN_KEY = "tp_admin_token";

const TABS = [
  { id: "applications", label: "განაცხადები", icon: Inbox },
  { id: "reservations", label: "დაჯავშნები", icon: CalendarCheck },
  { id: "waitlist", label: "რიგი", icon: Clock },
  { id: "vehicles", label: "ავტომობილები", icon: Car },
  { id: "categories", label: "კატეგორიები", icon: Image },
  { id: "site", label: "საიტის მართვა", icon: Globe },
  { id: "blog", label: "ბლოგი", icon: Newspaper },
  { id: "benefits", label: "შეღავათები", icon: BadgePercent },
  { id: "about", label: "ჩვენ შესახებ", icon: Info },
  { id: "notifications", label: "შეტყობინებები", icon: BellRing },
  { id: "password", label: "პაროლი", icon: KeyRound },
];

function PasswordSection({ auth }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("ახალი პაროლი მინიმუმ 6 სიმბოლო უნდა იყოს");
    if (form.new_password !== form.confirm) return toast.error("პაროლები არ ემთხვევა");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/password`, { current_password: form.current_password, new_password: form.new_password }, auth);
      toast.success("პაროლი წარმატებით შეიცვალა");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "შეცვლა ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-lux p-7 max-w-md space-y-5" data-testid="admin-password-form">
      <p className="text-sm text-gray-400">შეცვალე ადმინის პაროლი საიტის გამოქვეყნებამდე.</p>
      <input type="password" required data-testid="admin-current-password" value={form.current_password} onChange={set("current_password")} placeholder="მიმდინარე პაროლი" className={inputCls} />
      <input type="password" required minLength={6} data-testid="admin-new-password" value={form.new_password} onChange={set("new_password")} placeholder="ახალი პაროლი (მინ. 6 სიმბოლო)" className={inputCls} />
      <input type="password" required data-testid="admin-confirm-password" value={form.confirm} onChange={set("confirm")} placeholder="გაიმეორე ახალი პაროლი" className={inputCls} />
      <button type="submit" disabled={saving} data-testid="admin-password-save" className="btn-gold w-full disabled:opacity-60">
        {saving ? "ინახება..." : "პაროლის შეცვლა"}
      </button>
    </form>
  );
}

export default function Admin() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || null);
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState(params.get("tab") || "applications");
  const [vehicles, setVehicles] = useState([]);
  const waitlistVehicle = params.get("vehicle") || "";

  useEffect(() => {
    document.title = "ადმინი — taxipark.ge";
    let robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "noindex, nofollow");
    if (token) {
      axios
        .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => {
          axios
            .get(`${API}/admin/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setVehicles(r.data))
            .catch(() => {});
        })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        });
    }
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("წარმატებით შეხვედი");
    } catch {
      toast.error("არასწორი პაროლი");
    }
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="pt-28 pb-24 min-h-[70vh] flex items-start justify-center">
        <form onSubmit={login} className="card-lux p-8 sm:p-10 w-full max-w-md mx-4 mt-10" data-testid="admin-login-form">
          <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center mb-6">
            <Lock className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-bold text-white">ადმინის შესვლა</h1>
          <p className="text-sm text-gray-400 mt-2">ავტოპარკის მართვა მხოლოდ ადმინისტრატორისთვის.</p>
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="პაროლი"
            className="w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder-gray-500 mt-6 focus:outline-none focus:border-[#D4AF37]/60"
          />
          <button type="submit" data-testid="admin-login-submit" className="btn-gold w-full mt-5">შესვლა</button>
        </form>
      </main>
    );
  }

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  return (
    <main data-testid="admin-page" className="pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-2">ადმინ პანელი</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-white">ავტოპარკის მართვა</h1>
          </div>
          <button
            onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }}
            data-testid="admin-logout-btn"
            className="btn-ghost !py-2.5 !px-5 text-sm"
          >
            <LogOut className="w-4 h-4" /> გამოსვლა
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                tab === t.id
                  ? "bg-[#D4AF37] text-[#0A0B0E] border-[#D4AF37]"
                  : "bg-white/5 text-gray-300 border-white/15 hover:border-white/40"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "applications" && <AdminApplications auth={auth} />}
        {tab === "reservations" && <AdminRequests kind="reservations" auth={auth} vehicles={vehicles} />}
        {tab === "waitlist" && <AdminRequests kind="waitlist" auth={auth} vehicles={vehicles} initialVehicle={waitlistVehicle} />}
        {tab === "vehicles" && <AdminVehicles auth={auth} />}
        {tab === "categories" && <AdminCategories auth={auth} />}
        {tab === "site" && <AdminSiteSettings auth={auth} />}
        {tab === "blog" && <AdminBlog auth={auth} />}
        {tab === "benefits" && <AdminBenefits auth={auth} />}
        {tab === "about" && <AdminAbout auth={auth} />}
        {tab === "notifications" && <AdminNotifications auth={auth} />}
        {tab === "password" && <PasswordSection auth={auth} />}
      </div>
    </main>
  );
}
